import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  InputAdornment,
  TablePagination,
  Grid
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  createContramedidas,
  deleteContramedidas,
  getContramedidasPorEmpresa,
  updateContramedidas,
  getContramedidasGenerales,
  asociarContramedidasVulnerabilidades,
} from "../../services/contramedidas/contramedidasData";
import { getVulnerabilidadPorEmpresa } from "../../services/vulnerabilidades/vulnerabilidadesData";
import { useAuth } from "../../context/AuthContext";
import { toast } from "react-toastify";

const TablaContramedidas = () => {
  const [datos, setDatos] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [gestionModalOpen, setGestionModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [modificarIndex, setModificarIndex] = useState(-1);
  const { user } = useAuth();
  const emp_codigo = user?.emp_codigo;
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Estados para la gestión de asociaciones
  const [selectedContramedida, setSelectedContramedida] = useState("");
  const [selectedVulnerabilidad, setSelectedVulnerabilidad] = useState("");
  const [asociaciones, setAsociaciones] = useState([]);
  const [contramedidasGenerales, setContramedidasGenerales] = useState([]);
  const [vulnerabilidadesEmpresa, setVulnerabilidadesEmpresa] = useState([]);

  // Formulario para crear contramedidas
  const [formulario, setFormulario] = useState({
    FechaInicio: "",
    Identificacion: "",
    Nombre: "",
    Descripción: "",
    Observacion: "",
    FechaInactividad: null,
  });

  const datosFiltrados = Array.isArray(datos)
    ? datos.filter((dato) => {
      const texto = busqueda.toLowerCase();
      return dato.con_nombre && dato.con_nombre.toLowerCase().includes(texto);
    })
    : [];

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (emp_codigo) {
          const contramedidasData = await getContramedidasPorEmpresa(emp_codigo);
          setDatos(Array.isArray(contramedidasData) ? contramedidasData : []);
        }
      } catch (error) {
        console.error("Error fetching contramedidas:", error);
        toast.error("Error al cargar las contramedidas.");
        setDatos([]);
      }
    };

    fetchData();
  }, [emp_codigo]);

  // Cargar contramedidas generales y vulnerabilidades al abrir el modal de gestión
  useEffect(() => {
    if (gestionModalOpen) {
      const fetchContramedidasGenerales = async () => {
        try {
          const data = await getContramedidasGenerales();
          setContramedidasGenerales(Array.isArray(data) ? data : []);
        } catch (error) {
          console.error("Error fetching contramedidas generales:", error);
          toast.error("Error al cargar contramedidas generales.");
        }
      };

      const fetchVulnerabilidadesEmpresa = async () => {
        try {
          const data = await getVulnerabilidadPorEmpresa(emp_codigo);
          setVulnerabilidadesEmpresa(Array.isArray(data) ? data : []);
        } catch (error) {
          console.error("Error fetching vulnerabilidades:", error);
          toast.error("Error al cargar vulnerabilidades.");
        }
      };

      fetchContramedidasGenerales();
      fetchVulnerabilidadesEmpresa();
    }
  }, [gestionModalOpen, emp_codigo]);

  const handleBusquedaChange = (event) => {
    setBusqueda(event.target.value);
    setPage(0);
  };

  const handleDateChange = (newValue) => {
    const formattedDate = newValue ? dayjs(newValue).format("YYYY-MM-DD") : "";
    setFormulario({
      ...formulario,
      FechaInicio: formattedDate,
    });
  };

  const handleFechaInactividadChange = (newValue) => {
    const formattedDate = newValue ? dayjs(newValue).format("YYYY-MM-DD") : "";
    setFormulario({
      ...formulario,
      FechaInactividad: formattedDate,
    });
  };

  const handleGestion = () => {
    setFormulario({
      FechaInicio: dayjs().format("YYYY-MM-DD"),
      Identificacion: generarIdentificacion(),
      Nombre: "",
      Descripción: "",
      Observacion: "",
      FechaInactividad: dayjs().add(1, "year").format("YYYY-MM-DD"),
    });
    setAsociaciones([]);
    setSelectedContramedida("");
    setSelectedVulnerabilidad("");
    setGestionModalOpen(true);
  };

  const handleFormChange = (event) => {
    setFormulario({
      ...formulario,
      [event.target.name]: event.target.value,
    });
  };

  const handleCrearContramedida = async () => {
    const camposFaltantes = [];
    if (!formulario.FechaInicio) camposFaltantes.push("Fecha de Inicio");
    if (!formulario.Identificacion) camposFaltantes.push("Identificación");
    if (!formulario.Nombre) camposFaltantes.push("Nombre");
    if (!formulario.Descripción) camposFaltantes.push("Descripción");
    if (!formulario.Observacion) camposFaltantes.push("Observación");
    if (!formulario.FechaInactividad) camposFaltantes.push("Fecha de Inactividad");

    if (camposFaltantes.length > 0) {
      toast.warning(`Faltan campos obligatorios: ${camposFaltantes.join(", ")}`);
      return;
    }

    const dataToSend = {
      emp_codigo: emp_codigo,
      con_fecha_inicio: formulario.FechaInicio,
      con_identificacion: formulario.Identificacion,
      con_nombre: formulario.Nombre,
      con_descripcion: formulario.Descripción,
      con_observacion: formulario.Observacion,
      con_fecha_inactividad: formulario.FechaInactividad,
    };

    try {
      await createContramedidas(dataToSend);
      toast.success("Contramedida creada correctamente");
      const contramedidasData = await getContramedidasGenerales();
      setContramedidasGenerales(Array.isArray(contramedidasData) ? contramedidasData : []);
      setFormulario({
        FechaInicio: dayjs().format("YYYY-MM-DD"),
        Identificacion: generarIdentificacion(),
        Nombre: "",
        Descripción: "",
        Observacion: "",
        FechaInactividad: dayjs().add(1, "year").format("YYYY-MM-DD"),
      });
    } catch (error) {
      toast.error("Error al crear la contramedida.");
    }
  };

  const handleAsociar = () => {
    if (!selectedContramedida || !selectedVulnerabilidad) {
      toast.warning("Seleccione una contramedida y una vulnerabilidad.");
      return;
    }

    const listaContramedidas = Array.isArray(contramedidasGenerales) ? contramedidasGenerales : [];
    const listaVulnerabilidades = Array.isArray(vulnerabilidadesEmpresa) ? vulnerabilidadesEmpresa : [];

    setAsociaciones([
      ...asociaciones,
      {
        con_codigo: selectedContramedida,
        vul_codigo: selectedVulnerabilidad,
        con_nombre: listaContramedidas.find((c) => c.con_codigo === selectedContramedida)?.con_nombre || "N/A",
        vul_nombre: listaVulnerabilidades.find((v) => v.vul_codigo === selectedVulnerabilidad)?.vul_nombre || "N/A",
      },
    ]);
    setSelectedContramedida("");
    setSelectedVulnerabilidad("");
  };

  const handleEliminarAsociacion = (index) => {
    setAsociaciones(asociaciones.filter((_, i) => i !== index));
  };

  const handleConfirmarAsociaciones = async () => {
    if (asociaciones.length === 0) {
      toast.warning("No hay asociaciones para confirmar.");
      return;
    }

    try {
      await asociarContramedidasVulnerabilidades(asociaciones);
      toast.success("Asociaciones confirmadas correctamente");
      const contramedidasData = await getContramedidasPorEmpresa(emp_codigo);
      setDatos(Array.isArray(contramedidasData) ? contramedidasData : []);
      setGestionModalOpen(false);
      setAsociaciones([]);
      setSelectedContramedida("");
      setSelectedVulnerabilidad("");
    } catch (error) {
      toast.error("Error al confirmar las asociaciones.");
    }
  };

  const handleCancelarGestion = () => {
    setGestionModalOpen(false);
    setFormulario({
      FechaInicio: "",
      Identificacion: "",
      Nombre: "",
      Descripción: "",
      Observacion: "",
      FechaInactividad: null,
    });
    setAsociaciones([]);
    setSelectedContramedida("");
    setSelectedVulnerabilidad("");
  };

  const handleModificar = (index) => {
    setModificarIndex(datos[index].con_codigo);
    setFormulario({
      FechaInicio: datos[index].con_fecha_inicio ? datos[index].con_fecha_inicio.split("T")[0] : "",
      Identificacion: datos[index].con_identificacion,
      Nombre: datos[index].con_nombre,
      Descripción: datos[index].con_descripcion,
      Observacion: datos[index].con_observacion,
      FechaInactividad: datos[index].con_fecha_inactividad ? datos[index].con_fecha_inactividad.split("T")[0] : null,
    });
    setEditModalOpen(true);
  };

  const handleGuardarEdicion = async () => {
    const camposFaltantes = [];
    if (!formulario.FechaInicio) camposFaltantes.push("Fecha de Inicio");
    if (!formulario.Identificacion) camposFaltantes.push("Identificación");
    if (!formulario.Nombre) camposFaltantes.push("Nombre");
    if (!formulario.Descripción) camposFaltantes.push("Descripción");
    if (!formulario.Observacion) camposFaltantes.push("Observación");
    if (!formulario.FechaInactividad) camposFaltantes.push("Fecha de Inactividad");

    if (camposFaltantes.length > 0) {
      toast.warning(`Faltan campos obligatorios: ${camposFaltantes.join(", ")}`);
      return;
    }

    const dataToSend = {
      con_fecha_inicio: formulario.FechaInicio,
      con_identificacion: formulario.Identificacion,
      con_nombre: formulario.Nombre,
      con_descripcion: formulario.Descripción,
      con_observacion: formulario.Observacion,
      con_fecha_inactividad: formulario.FechaInactividad,
    };

    try {
      await updateContramedidas(modificarIndex, dataToSend);
      toast.success("Contramedida modificada correctamente");
      const contramedidasData = await getContramedidasPorEmpresa(emp_codigo);
      setDatos(contramedidasData);
      setEditModalOpen(false);
      setFormulario({
        FechaInicio: "",
        Identificacion: "",
        Nombre: "",
        Descripción: "",
        Observacion: "",
        FechaInactividad: null,
      });
    } catch (error) {
      toast.error("Error al guardar la contramedida.");
    }
  };

  const handleCancelarEdicion = () => {
    setEditModalOpen(false);
    setFormulario({
      FechaInicio: "",
      Identificacion: "",
      Nombre: "",
      Descripción: "",
      Observacion: "",
      FechaInactividad: null,
    });
  };

  const handleEliminar = (index) => {
    setDeleteIndex(index);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    const codigoToDelete = Array.isArray(datos) ? datos[deleteIndex]?.con_codigo : undefined;
    if (!codigoToDelete) {
      toast.error("No se encontró la contramedida a eliminar.");
      setConfirmOpen(false);
      setDeleteIndex(null);
      return;
    }
    try {
      await deleteContramedidas(codigoToDelete);
      toast.success("Contramedida eliminada correctamente");
      const contramedidasData = await getContramedidasPorEmpresa(emp_codigo);
      setDatos(Array.isArray(contramedidasData) ? contramedidasData : []);
      setConfirmOpen(false);
      setDeleteIndex(null);
    } catch (error) {
      setConfirmOpen(false);
      const errorMsg =
        error?.message ||
        error?.response?.data?.message ||
        "No se puede eliminar la contramedida porque está asociada en el sistema.";
      if (
        error?.response?.data?.errno === 1451 ||
        (error?.response?.data?.code && error.response.data.code.includes("ER_ROW_IS_REFERENCED")) ||
        error?.status === 400 ||
        errorMsg.toLowerCase().includes("no se puede eliminar") ||
        errorMsg.toLowerCase().includes("asociada")
      ) {
        setErrorMessage(errorMsg);
        setErrorModalOpen(true);
      } else {
        toast.error(errorMsg);
      }
      setDeleteIndex(null);
    }
  };

  const handleCancelDelete = () => {
    setConfirmOpen(false);
    setDeleteIndex(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  function generarIdentificacion() {
    const letras = Array(3)
      .fill(0)
      .map(() => String.fromCharCode(65 + Math.floor(Math.random() * 26)))
      .join("");
    const numeros = String(Math.floor(Math.random() * 1000)).padStart(3, "0");
    return `CM-${letras}-${numeros}`;
  }

  return (
    <Box>
      {/* Toolbar */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          borderBottom: '1px solid #e0e0e0'
        }}
      >
        <TextField
          variant="outlined"
          size="small"
          placeholder="Buscar por nombre..."
          value={busqueda}
          onChange={handleBusquedaChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: { xs: '100%', sm: 300 } }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleGestion}
          sx={{
            backgroundColor: '#001F3F',
            color: '#fff',
            '&:hover': { backgroundColor: '#003366' },
            textTransform: 'none',
            fontWeight: 500
          }}
        >
          Gestionar Contramedida
        </Button>
      </Box>

      <TableContainer>
        <Table sx={{ minWidth: 750 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600, color: '#001F3F' }}>Vulnerabilidad</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#001F3F' }}>Amenaza</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#001F3F' }}>Fecha Inicio</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#001F3F' }}>Identificación</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#001F3F' }}>Nombre</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#001F3F' }}>Descripción</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#001F3F' }}>Observación</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#001F3F' }}>Fecha Inactividad</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#001F3F' }} align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {datosFiltrados.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                  <Typography variant="body1" sx={{ color: '#666' }}>
                    {datos.length === 0 ? "No hay contramedidas registradas." : "No se encontraron resultados."}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              datosFiltrados
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((dato, index) => (
                  <TableRow key={index} hover>
                    <TableCell sx={{ color: "#3A6B7E", fontWeight: "bold" }}>{dato.vul_nombre || "N/A"}</TableCell>
                    <TableCell sx={{ color: "#d32f2f", fontWeight: "bold" }}>{dato.ame_nombre || "N/A"}</TableCell>
                    <TableCell>{dato.con_fecha_inicio ? dato.con_fecha_inicio.split("T")[0] : ""}</TableCell>
                    <TableCell>{dato.con_identificacion}</TableCell>
                    <TableCell>{dato.con_nombre}</TableCell>
                    <TableCell>{dato.con_descripcion}</TableCell>
                    <TableCell>{dato.con_observacion}</TableCell>
                    <TableCell>{dato.con_fecha_inactividad ? dato.con_fecha_inactividad.split("T")[0] : ""}</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <IconButton onClick={() => handleModificar(index + (page * rowsPerPage))} size="small" sx={{ color: "#666" }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton onClick={() => handleEliminar(index + (page * rowsPerPage))} size="small" sx={{ color: "#d32f2f" }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={datosFiltrados.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Filas por página:"
      />

      {/* Modal para gestionar contramedidas y asociaciones */}
      <Dialog
        open={gestionModalOpen}
        onClose={handleCancelarGestion}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ pb: 2, fontWeight: 600, color: '#001F3F', fontSize: '1.25rem' }}>
          Gestionar Contramedidas
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Sección izquierda: Crear contramedida */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 2, backgroundColor: '#F4F6F8', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold", color: "#001F3F" }}>
                  Crear Nueva Contramedida
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <DatePicker
                      label="Fecha de Inicio"
                      name="FechaInicio"
                      value={formulario.FechaInicio ? dayjs(formulario.FechaInicio) : null}
                      onChange={handleDateChange}
                      minDate={dayjs()}
                      slotProps={{ textField: { fullWidth: true, size: "small", variant: "outlined" } }}
                    />
                    <TextField
                      label="Identificación"
                      name="Identificacion"
                      fullWidth
                      value={formulario.Identificacion}
                      onChange={handleFormChange}
                      size="small"
                      variant="outlined"
                    />
                    <TextField
                      label="Nombre"
                      name="Nombre"
                      fullWidth
                      value={formulario.Nombre}
                      onChange={handleFormChange}
                      size="small"
                      variant="outlined"
                    />
                    <TextField
                      label="Descripción"
                      name="Descripción"
                      fullWidth
                      multiline
                      rows={3}
                      value={formulario.Descripción}
                      onChange={handleFormChange}
                      size="small"
                      variant="outlined"
                    />
                    <TextField
                      label="Observación"
                      name="Observacion"
                      fullWidth
                      multiline
                      rows={2}
                      value={formulario.Observacion}
                      onChange={handleFormChange}
                      size="small"
                      variant="outlined"
                    />
                    <DatePicker
                      label="Fecha de Inactividad"
                      name="FechaInactividad"
                      value={formulario.FechaInactividad ? dayjs(formulario.FechaInactividad) : null}
                      onChange={handleFechaInactividadChange}
                      slotProps={{ textField: { fullWidth: true, size: "small", variant: "outlined" } }}
                    />
                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor: '#001F3F',
                        color: '#fff',
                        '&:hover': { backgroundColor: '#003366' },
                        mt: 1,
                        textTransform: 'none',
                        fontWeight: 500
                      }}
                      onClick={handleCrearContramedida}
                    >
                      Crear Contramedida
                    </Button>
                  </Box>
                </LocalizationProvider>
              </Paper>
            </Grid>

            {/* Sección derecha: Asociar contramedidas con vulnerabilidades */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 2, backgroundColor: '#F4F6F8', borderRadius: 2, height: '100%' }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold", color: "#001F3F" }}>
                  Asociar a Vulnerabilidad
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Select
                    value={selectedContramedida}
                    onChange={(e) => setSelectedContramedida(e.target.value)}
                    fullWidth
                    displayEmpty
                    size="small"
                    variant="outlined"
                    sx={{ backgroundColor: '#fff' }}
                  >
                    <MenuItem value="" disabled>
                      Seleccionar Contramedida
                    </MenuItem>
                    {(Array.isArray(contramedidasGenerales) ? contramedidasGenerales : []).map((cm) => (
                      <MenuItem key={cm.con_codigo} value={cm.con_codigo}>
                        {cm.con_nombre}
                      </MenuItem>
                    ))}
                  </Select>
                  <Select
                    value={selectedVulnerabilidad}
                    onChange={(e) => setSelectedVulnerabilidad(e.target.value)}
                    fullWidth
                    displayEmpty
                    size="small"
                    variant="outlined"
                    sx={{ backgroundColor: '#fff' }}
                  >
                    <MenuItem value="" disabled>
                      Seleccionar Vulnerabilidad
                    </MenuItem>
                    {(Array.isArray(vulnerabilidadesEmpresa) ? vulnerabilidadesEmpresa : []).map((vul) => (
                      <MenuItem key={vul.vul_codigo} value={vul.vul_codigo}>
                        {vul.vul_nombre}
                      </MenuItem>
                    ))}
                  </Select>
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: '#001F3F',
                      color: '#fff',
                      '&:hover': { backgroundColor: '#003366' },
                      textTransform: 'none',
                      fontWeight: 500
                    }}
                    onClick={handleAsociar}
                  >
                    Asociar
                  </Button>

                  <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: "bold", color: "#001F3F" }}>
                    Asociaciones Seleccionadas
                  </Typography>
                  <List sx={{ maxHeight: 200, overflow: "auto", bgcolor: "white", borderRadius: 1, border: "1px solid #ddd" }}>
                    {asociaciones.length === 0 ? (
                      <ListItem>
                        <ListItemText primary="No hay asociaciones seleccionadas" />
                      </ListItem>
                    ) : (
                      asociaciones.map((asoc, index) => (
                        <ListItem key={index} divider>
                          <ListItemText
                            primary={asoc.con_nombre}
                            secondary={`→ ${asoc.vul_nombre}`}
                          />
                          <ListItemSecondaryAction>
                            <IconButton edge="end" onClick={() => handleEliminarAsociacion(index)}>
                              <DeleteIcon sx={{ color: "#d32f2f" }} />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))
                    )}
                  </List>
                  <Button
                    variant="contained"
                    sx={{
                      mt: 2,
                      backgroundColor: '#0f8a5f',
                      color: '#fff',
                      '&:hover': { backgroundColor: '#0c6c4b' },
                      textTransform: 'none'
                    }}
                    onClick={handleConfirmarAsociaciones}
                  >
                    Confirmar Asociaciones
                  </Button>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCancelarGestion} sx={{ color: '#666' }}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Modal para editar contramedidas */}
      <Dialog
        open={editModalOpen}
        onClose={handleCancelarEdicion}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 2, fontWeight: 600, color: '#001F3F', fontSize: '1.25rem' }}>
          Modificar Contramedida
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <DatePicker
                label="Fecha de Inicio"
                name="FechaInicio"
                value={formulario.FechaInicio ? dayjs(formulario.FechaInicio) : null}
                onChange={handleDateChange}
                minDate={dayjs()}
                slotProps={{ textField: { fullWidth: true, size: "small", variant: "outlined" } }}
              />
              <TextField
                label="Identificación"
                name="Identificacion"
                fullWidth
                value={formulario.Identificacion}
                onChange={handleFormChange}
                size="small"
                variant="outlined"
              />
              <TextField
                label="Nombre"
                name="Nombre"
                fullWidth
                value={formulario.Nombre}
                onChange={handleFormChange}
                size="small"
                variant="outlined"
              />
              <TextField
                label="Descripción"
                name="Descripción"
                fullWidth
                multiline
                rows={3}
                value={formulario.Descripción}
                onChange={handleFormChange}
                size="small"
                variant="outlined"
              />
              <TextField
                label="Observación"
                name="Observacion"
                fullWidth
                multiline
                rows={2}
                value={formulario.Observacion}
                onChange={handleFormChange}
                size="small"
                variant="outlined"
              />
              <DatePicker
                label="Fecha de Inactividad"
                name="FechaInactividad"
                value={formulario.FechaInactividad ? dayjs(formulario.FechaInactividad) : null}
                onChange={handleFechaInactividadChange}
                slotProps={{ textField: { fullWidth: true, size: "small", variant: "outlined" } }}
              />
            </Box>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCancelarEdicion} sx={{ color: '#666' }}>Cancelar</Button>
          <Button
            onClick={handleGuardarEdicion}
            variant="contained"
            sx={{
              backgroundColor: '#001F3F',
              color: '#fff',
              '&:hover': { backgroundColor: '#003366' }
            }}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de confirmación para eliminación */}
      <Dialog
        open={confirmOpen}
        onClose={handleCancelDelete}
      >
        <DialogTitle sx={{ fontWeight: 600, color: '#001F3F', fontSize: '1.25rem' }}>
          Confirmar Eliminación
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#666' }}>
            ¿Está seguro que desea eliminar esta contramedida?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCancelDelete} sx={{ color: '#666' }}>Cancelar</Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{
              backgroundColor: '#d32f2f',
              color: '#fff',
              '&:hover': { backgroundColor: '#c62828' }
            }}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de error */}
      <Dialog
        open={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, color: '#001F3F', fontSize: '1.25rem' }}>
          No se puede eliminar
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Typography variant="body1" sx={{ color: '#666', mb: 2 }}>
              {errorMessage || 'Esta contramedida está siendo utilizada y no puede eliminarse.'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#999' }}>
              Antes de eliminarla, elimina o actualiza las relaciones donde se utiliza.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setErrorModalOpen(false)}
            variant="contained"
            sx={{ backgroundColor: '#001F3F', color: '#fff', '&:hover': { backgroundColor: '#003366' } }}
          >
            Entendido
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TablaContramedidas;